import { Elements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import _ from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { EventFeature, EventTypeEnum, BoolEnum } from '../../../API';
import { Logo } from '../../../assets/icons';
import { Card } from '../../../components';
import { Button } from '../../../components/button/Button';
import { FeatureListItem } from '../../../components/feature-list-item/FeatureListItem';
import { Colors } from '../../../constants';
import useGetUpcomingEvents from '../../../hooks/useGetUpcomingEvents';
import useGetEventSession from '../../../hooks/useGetEventSession';
import UserContext from '../../../contexts/UserContext';
import { addEventSessionAttendeeToChatChannel, saveEventSessionAttendee } from '../../../services/events';
import useActiveCampaign from '../../../hooks/useActiveCampaign';
import { addACTag } from '../../../services/activeCampaign';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string);

const StripeCheck = ({
  clientSecret,
  eventSessionData,
  currentUser,
  rawPaymentLog,
}: {
  clientSecret: string;
  eventSessionData: any;
  currentUser: any;
  rawPaymentLog?: string;
}) => {
  const stripe = useStripe();
  const activeCampaign = useActiveCampaign();

  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!stripe || !currentUser || !eventSessionData) {
      return;
    }

    const saveAttendee = async (currentUser: any, eventSession: any, rawPaymentLog: string | null = null) => {
      await saveEventSessionAttendee(
        {
          userId: currentUser?.user?.id,
          eventSessionId: eventSession?.id,
          eventId: eventSession?.eventId,
          confirmed: BoolEnum.FALSE,
          metadata: {
            paymentTimestamp: Math.round(Date.now() / 1000),
            rawPaymentLog,
          },
        },
        { forceCreate: true }
      );
    };

    stripe.retrievePaymentIntent(clientSecret).then(async ({ paymentIntent }) => {
      switch (paymentIntent?.status) {
        case 'succeeded':
          setMessage('Payment succeeded!');
          //add the registration to the database
          await saveAttendee(currentUser, eventSessionData, JSON.stringify(paymentIntent) as string);
          //add the user to the event session chat channel
          await addEventSessionAttendeeToChatChannel(currentUser?.user?.id, eventSessionData);
          //save the active campaign tags
          try {
            const { firstName, lastName, email, phone } = currentUser?.user;
            await addACTag({
              user: { firstName, lastName, email, phone },
              tags: `${eventSessionData?.event?.metadata?.activeCampaignTagId ?? 364},${eventSessionData?.metadata?.activeCampaignTagId}`,
            });
          } catch (error) {}
          window.location.replace('https://www.weflourish.com/sgp-confirmation');
          break;
        case 'processing':
          setMessage('Your payment is processing.');
          break;
        case 'requires_payment_method':
          setMessage('Your payment was not successful, please try again.');
          break;
        default:
          setMessage('Something went wrong.');
          break;
      }
    });
  }, [clientSecret, currentUser, eventSessionData, stripe, activeCampaign]);

  return (
    <div>
      <h1 className="text-4xl">{message}</h1>
    </div>
  );
};

const GroupRegistrationConfirmation = () => {
  const [phoneContinue, setphoneContinue] = useState(false);
  const { data } = useGetUpcomingEvents(EventTypeEnum.SMALL_GROUP);
  const [secret, setSecret] = useState<string | undefined>();
  const [eventSessionId, setEventSessionId] = useState<string | null>(null);
  const { currentUser } = useContext(UserContext);

  const { data: eventSessionData } = useGetEventSession(eventSessionId as string);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get('payment_intent_client_secret');
    setEventSessionId(params.get('esid'));

    if (!clientSecret) {
      return;
    }

    setSecret(clientSecret);
  }, []);

  return (
    <div className="grid md:grid-cols-2">
      <div className="h-screen flex justify-center bg-stone drop-shadow-2xl xs:p-5 sm:p-10 lg:p-20 xs:hidden md:block">
        <div className="flex items-start justify-start flex-col">
          <div className="flex items-start justify-start w-full mb-10">
            <Logo width="120" fill={Colors.ginger} />
          </div>
          <div className="mt-10">
            <h1 className="text-[2.5rem] leading-tight font-analogue">
              Get full access to <br />
              Connection Club.
            </h1>
            <span className="leading-loose font-thin">8+ hours of content (+ bonus perks!) for $29</span>
          </div>
          {_.has(data, [0, 'features']) && (
            <div className="mt-10">
              <div className="font-bold mb-2">Your purchase includes:</div>

              {_.get(data, [0, 'features']).map((item: EventFeature, k: number) => (
                <FeatureListItem index={0} title={item.title} description={item.description as string} key={k} />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="h-screen bg-gradient-to-br from-periwinkle-200 via-meringue100 to-blush-100 flex flex-col items-center justify-center p-10">
        <div className="mb-20 md:hidden xs:block">
          <Logo width="140" fill={Colors.ginger} />
        </div>
        {!phoneContinue && (
          <Card classes="md:hidden xs:block">
            <div className="text-center">
              <h1 className="text-[2.5rem] leading-tight font-analogue">
                Get full access to <br />
                Connection Club.
              </h1>
              <span className="leading-loose font-thin">8+ hours of content (+ bonus perks!) for $29</span>
            </div>
            {_.has(data, [0, 'features']) && (
              <div className="mt-10">
                <div className="font-bold mb-2">Your purchase includes:</div>

                {_.get(data, [0, 'features'], []).map((item: EventFeature, k: number) => (
                  <FeatureListItem index={0} title={item.title} description={item.description as string} key={k} />
                ))}
              </div>
            )}
            <div className="mt-10">
              <Button text="Continue" onClick={() => setphoneContinue(true)} primary classes="font-semibold" />
            </div>
          </Card>
        )}
        {secret && (
          <Card>
            <Elements stripe={stripePromise} options={{ clientSecret: secret }}>
              <StripeCheck clientSecret={secret as string} eventSessionData={eventSessionData} currentUser={currentUser} />
            </Elements>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GroupRegistrationConfirmation;
